import { useGetSystemType } from "@features/ax/assetRegistry/systems/hooks";
import {
  useAssetType,
  useFacilityTypeOptions,
  useNetworkTypeOptions,
} from "@features/ax/hooks";
import { useElectricUnit, useFlowCapacityUnit } from "@features/wx/hooks";
import { CreateMasterSopRequest } from "@features/wx/master/types";
import { useOperationTypes } from "@features/wx/settings/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom"; // Added for URL detection
import * as z from "zod";
import {
  useCreateSop,
  useServiceAssociationOptions,
  useSLARulesOptions,
  useSopSubtypeOptions,
  useSopTypeOptions,
  useTaxRates,
  useUpdateSop,
  useUtilityServiceOptions,
} from "../../../hooks";

// Schema for validation
const basicInfoSchema = z
  .object({
    sopName: z.string().min(3, "SOP name must be at least 3 characters"),
    shortName: z.string().min(2, "Short name must be at least 2 characters"),
    description: z.string().min(1, "Description is required"),
    utilityType: z.string().min(1, "Please select a utility type"),
    type: z.string().min(1, "Please select a type"),
    typeCode: z.string().optional(),
    subType: z.string().min(1, "Please select a subtype"),
    subTypeCode: z.string().optional(),
    associatedWith: z.string().min(1, "Please select an association"),
    sla: z.string().min(1, "SLA is required"),
    estimatedLabourCost: z.string().min(1, "Estimated labour cost is required"),
    estimatedOtherCost: z.string().min(1, "Estimated other cost is required"),
    taxRate: z.string().min(1, "Tax rate is required"),
    requiredFieldforceCount: z
      .string()
      .min(1, "Required fieldforce count is required"),
    customAssetType: z.string().optional(),
    actionType: z.string().optional(),
    customActionType: z.string().optional(),
    operationType: z.string().min(1, "Please select an operation type"),
    customOperationType: z.string().optional(),
    customType: z.string().optional(),
    customSubType: z.string().optional(),
    // slaHrs: z.string().min(1, "Please enter SLA hours"),
    assetType: z.array(z.string()).optional(),
    systemType: z.array(z.string()).optional(),
    facilityType: z.array(z.string()).optional(),
    networkType: z.array(z.string()).optional(),
    unit: z.string().optional(),
    basicInfoUpdated: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.associatedWith === "2") {
        // "2" is the key for Asset

        if (
          data.actionType === "custom" &&
          (!data.customActionType || data.customActionType.length === 0)
        ) {
          return false;
        }
        if (
          data.operationType === "custom" &&
          (!data.customOperationType || data.customOperationType.length === 0)
        ) {
          return false;
        }

        return (
          data.assetType &&
          data.assetType.length > 0 &&
          data.operationType &&
          data.operationType.length > 0
        );
      }
      // ✅ ADD FACILITY VALIDATION
      if (data.associatedWith === "3") {
        return data.facilityType && data.facilityType.length > 0;
      }

      // ✅ ADD SYSTEM VALIDATION
      if (data.associatedWith === "4") {
        return data.systemType && data.systemType.length > 0;
      }

      // ✅ ADD NETWORK VALIDATION
      if (data.associatedWith === "5") {
        return data.networkType && data.networkType.length > 0;
      }
      return true;
    },
    {
      message: "Please fill all the required fields",
      path: ["associatedWith"],
    },
  )
  .refine(
    (data) => {
      if (["30", "27", "33", "35"].includes(data.operationType || "")) {
        return data.unit && data.unit.length > 0;
      }
      return true;
    },
    {
      message: "Unit is required for the selected operation type",
      path: ["unit"],
    },
  );

interface SopBasicInfoStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
  //  Removed isEditMode and sopId props - we'll detect them automatically
}

const SopBasicInfoStep: React.FC<SopBasicInfoStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  onStepSave,
}) => {
  const currency = useCurrency();
  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const location = useLocation(); //  Get current location
  const navigate = useNavigate();
  const { data: assetTypeList } = useAssetType();

  const assetTypeOptions = useMemo(() => {
    return (
      assetTypeList?.result?.map((assetType: any) => ({
        value: assetType.code,
        label: assetType.name,
      })) || []
    );
  }, [assetTypeList]);

  const { data: networkList } = useNetworkTypeOptions();

  const networkOptions = useMemo(
    () =>
      networkList?.map((item: any) => ({
        value: item.code,
        label: item.name,
      })) || [],
    [networkList],
  );

  const { data: flowcapacityUnitList } = useFlowCapacityUnit();
  const flowCapacityUnitOptions = useMemo(() => {
    return (
      flowcapacityUnitList?.map((unit: any) => ({
        value: unit.key,
        label: unit.value,
      })) || []
    );
  }, [flowcapacityUnitList]);

  const { data: electricUnitList } = useElectricUnit();
  const electricUnitOptions = useMemo(() => {
    return (
      electricUnitList?.map((unit: any) => ({
        value: unit.key,
        label: unit.value,
      })) || []
    );
  }, [electricUnitList]);

  const { data: systemList = [] } = useGetSystemType("system_type", true);

  const systemOptions = useMemo(() => {
    return (
      systemList.map((choice) => ({
        value: choice.code,
        label: choice.name,
      })) || []
    );
  }, [systemList]);

  const { data: facilityTypeData } = useFacilityTypeOptions();
  const facilityTypeOptions = useMemo(
    () =>
      facilityTypeData?.map((data: any) => ({
        value: data.code,
        label: data.name,
      })) || [],
    [facilityTypeData],
  );

  //  Automatically detect mode and sopId from URL
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || "create";
  const sopId = queryParams.get("id") || "";

  //  Determine if we're in edit mode based on URL params
  const isEditMode = mode === "edit" && sopId;

  //  Conditionally use hooks based on detected mode
  const createSopMutation = useCreateSop();
  const updateSopMutation = useUpdateSop();

  // Fetch dynamic data
  const [selectedTypeCode, setSelectedTypeCode] = useState(
    state.basicInfo?.typeCode || "",
  );

  const { data: taxData } = useTaxRates();

  // Move getTaxCodeFromApi above its first usage
  const getTaxCodeFromApi = (apiTax: string) => {
    if (!taxData?.result) return apiTax;
    // If apiTax is already a code, return as is
    if (taxData.result.some((tax: any) => tax.code === apiTax)) return apiTax;
    // Try to find by name
    const found = taxData.result.find((tax: any) => tax.name === apiTax);
    return found ? found.code : apiTax;
  };

  const basicInfoForm = useForm<z.infer<typeof basicInfoSchema>>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      sopName: state.basicInfo?.sopName || "",
      shortName: state.basicInfo?.shortName || "",
      description: state.basicInfo?.description || "",
      utilityType: state.basicInfo?.utilityType || "",
      type: state.basicInfo?.type || "",
      typeCode: state.basicInfo?.typeCode || "",
      subType: state.basicInfo?.subType || "",
      subTypeCode: state.basicInfo?.subTypeCode || "",
      associatedWith: state.basicInfo?.associatedWith || "",
      sla: state.basicInfo?.sla || "",
      estimatedLabourCost: state.basicInfo?.estimatedLabourCost || "",
      estimatedOtherCost: state.basicInfo?.estimatedOtherCost || "",  
      taxRate: getTaxCodeFromApi(state.basicInfo?.taxRate || ""),
      requiredFieldforceCount: state.basicInfo?.requiredFieldforceCount || "",
      assetType: state.basicInfo?.assetType || [],
      customAssetType: state.basicInfo?.customAssetType || "",
      actionType: state.basicInfo?.actionType || "",
      customActionType: state.basicInfo?.customActionType || "",
      operationType: state.basicInfo?.operationType || "",
      customOperationType: state.basicInfo?.customOperationType || "",
      customType: state.basicInfo?.customType || "",
      customSubType: state.basicInfo?.customSubType || "",
      systemType: state.basicInfo?.systemType || [],
      facilityType: state.basicInfo?.facilityType || [],
      networkType: state.basicInfo?.networkType || [],
      unit: state.basicInfo?.unit || "",

      // slaHrs: state.basicInfo?.slaHrs || "",

      basicInfoUpdated: state.basicInfo?.basicInfoUpdated || false,
    },
    mode: "onBlur",
  });

  const { options: associationOptions } = useServiceAssociationOptions();

  const { options: utilityOptions } = useUtilityServiceOptions();
  const { options: typeOptions } = useSopTypeOptions();
  const { options: subtypeOptions } = useSopSubtypeOptions(selectedTypeCode);
  const { options: SLARulesOptions } = useSLARulesOptions();
  const { data: operationTypes = [] } = useOperationTypes();
  // Helper to get tax options
  const getTaxRateOptions = () => {
    return (
      taxData?.result?.map((tax: any) => ({
        value: tax.code,
        label: tax.name,
      })) || []
    );
  };
  // Get type options with custom option
  const getTypeOptions = () => {
    const options = [...typeOptions];
    return options;
  };

  const getSLARulesOptions = () => {
    const options = [...SLARulesOptions];
    return options;
  };

  // Transform operation types to match FormField options format
  const getOperationTypeOptions = () => {
    const transformedOptions = operationTypes.map((opType: any) => ({
      value: opType.key,
      label: opType.value,
      disabled: opType.disabled || false,
      description: opType.description || undefined,
    }));
    return transformedOptions;
  };

  // Get subtype options based on selected type
  const getSubtypeOptions = () => {
    if (!selectedTypeCode) {
    }
    const options = [...subtypeOptions];
    return options;
  };

  // Check if asset fields should be shown
  const showAssetFields = basicInfoForm.watch("associatedWith") === "2"; // "2" is the key for Asset
  const showFacilityFields = basicInfoForm.watch("associatedWith") === "3";
  const showSystemFields = basicInfoForm.watch("associatedWith") === "4";
  const showNetworkFields = basicInfoForm.watch("associatedWith") === "5";
  const opType = basicInfoForm.watch("operationType");
  const showUnit = ["30", "27", "33", "35"].includes(opType);
  const finalUnitOptions = useMemo(() => {
    if (opType === "35") {
      return electricUnitOptions;
    }
    return flowCapacityUnitOptions;
  }, [showUnit, opType]);
  // Same transformation function for both create and edit
  const transformFormDataToApiFormat = useCallback(
    (
      formData: z.infer<typeof basicInfoSchema>,
      isUpdate: boolean = false,
    ): CreateMasterSopRequest & {
      // sla_hours?: string;
      basicInfoUpdated?: boolean;
      // ✅ NEW: Add labels for display purposes
      displayLabels?: {
        utilityTypeLabel?: string;
        associatedWithLabel?: string;
        typeLabel?: string;
        subTypeLabel?: string;
        operationTypeLabel?: string;
        slaLabel?: string;
        taxRateLabel?: string;
      };
    } => {
      // Handle utility services - convert single value to array
      const utilityServices = [formData.utilityType];

      // ✅ Enhanced association type mapping with label lookup
      const associationTypeMap: {
        [key: string]: { value: number; label: string };
      } = {
        "0": { value: 0, label: "Consumer" },
        "1": { value: 1, label: "Meter" },
        "2": { value: 2, label: "Asset" },
        "3": { value: 3, label: "Complaint" },
        "4": { value: 4, label: "Generic" },
      };

      const associationInfo = associationTypeMap[formData.associatedWith];
      const sopAssociationType = formData.associatedWith;

      // ✅ Enhanced SOP type handling with label lookup
      let sopType = formData.type;
      let sopTypeLabel = formData.type;
      if (formData.type !== "custom") {
        sopType = `${formData.type}`;
        // Find the label from typeOptions
        const typeOption = typeOptions.find(
          (option) => option.value == formData.type,
        );
        sopTypeLabel = typeOption?.label;
      }

      // ✅ Enhanced SOP subtype handling with label lookup
      let sopSubtype = formData.subType;
      let sopSubtypeLabel = formData.subType;

      if (formData.subType !== "custom") {
        sopSubtype = `${formData.subType}`;
        // Find the label from subtypeOptions
        const subtypeOption = subtypeOptions.find(
          (option) => option.value == formData.subType,
        );
        sopSubtypeLabel = subtypeOption?.label;
      }

      // ✅ Enhanced operation type handling with label lookup
      let operationType = 1; // Default value
      let operationTypeLabel = "Default Operation";

      if (formData.operationType) {
        const parsedOpType = parseInt(formData.operationType);
        if (!isNaN(parsedOpType)) {
          operationType = parsedOpType;
        }
        // Find the label from operation types
        const operationOption = getOperationTypeOptions().find((option) => {
          return option.value == formData.operationType;
        });
        operationTypeLabel = operationOption?.label;
      }

      // ✅ Enhanced tax handling with label lookup
      const tax = formData.taxRate;
      let taxLabel = formData.taxRate;

      if (tax) {
        const taxOption = getTaxRateOptions().find(
          (option) => option.value === tax,
        );
        taxLabel = taxOption?.label || tax;
      }

      // ✅ Enhanced utility type handling with label lookup
      let utilityTypeLabel = formData.utilityType;
      const utilityOption = utilityOptions.find(
        (option) => option.value === formData.utilityType,
      );
      if (utilityOption) {
        utilityTypeLabel = utilityOption.label;
      }

      // ✅ Enhanced SLA handling with label lookup
      let slaLabel = formData.sla;
      const slaOption = getSLARulesOptions().find(
        (option) => option.value.toString() === formData.sla,
      );
      if (slaOption) {
        slaLabel = slaOption.label;
      }

      // Handle rules
      const rules: number[] = [];
      if (formData.sla) {
        const slaId = parseInt(formData.sla);
        if (!isNaN(slaId)) {
          rules.push(slaId);
        }
      }

      const additionalData: any = {};

      // Add system type if present

      if (formData.systemType && formData.systemType.length > 0) {
        additionalData.systemTypes = formData.systemType;
      }

      // Add facility type if present
      if (formData.facilityType && formData.facilityType.length > 0) {
        additionalData.facilityTypes = formData.facilityType;
      }

      // Add network type if present
      if (formData.networkType && formData.networkType.length > 0) {
        additionalData.networkTypes = formData.networkType;
      }

      if (formData.systemType && formData.systemType.length > 0) {
        additionalData.systemTypes = formData.systemType;
      }

      // Add facility type if present
      if (formData.facilityType && formData.facilityType.length > 0) {
        additionalData.facilityTypes = formData.facilityType;
      }

      // Add network type if present
      if (formData.assetType && formData.assetType.length > 0) {
        additionalData.assetTypes = formData.assetType;
      }

      // Add unit if present
      if (formData.unit) {
        additionalData.unit = formData.unit;
      }

      // const slaHrsValue = basicInfoForm.getValues("slaHrs");

      return {
        name: formData.sopName,
        shortName: formData.shortName,
        description: formData.description,
        utilityServices: utilityServices,
        sopAssociationType: Number(sopAssociationType),
        sopType: sopType,
        sopSubtype: sopSubtype,
        rulesDuration: formData.sla || "0",
        rate: formData.estimatedLabourCost,
        estimatedOtherCost: formData.estimatedOtherCost,
        tax: tax,
        staffRequired: formData.requiredFieldforceCount,
        rules: rules,
        operationType: operationType,
        remoteUtilityId: parseInt(utilityId || "0"),
        additionalData,
        // ✅ NEW: Include labels for display purposes
        displayLabels: {
          utilityTypeLabel,
          associatedWithLabel: associationInfo?.label,
          typeLabel: sopTypeLabel,
          subTypeLabel: sopSubtypeLabel,
          operationTypeLabel,
          slaLabel,
          taxRateLabel: taxLabel,
        },

        // ...(slaHrsValue ? { sla_hours: slaHrsValue } : {}),
        ...(isUpdate ? { basicInfoUpdated: true } : {}),
      };
    },
    [
      utilityId,
      typeOptions,
      subtypeOptions,
      utilityOptions,
      getSLARulesOptions,
      getTaxRateOptions,
      getOperationTypeOptions,
    ],
  );

  // Define form fields using FormField system
  const basicInfoFormFields: FormField[] = useMemo(
    () => [
      {
        name: "sopName",
        label: "SOP Name",
        type: "text",
        required: true,
        placeholder: "e.g. Water Meter Installation",
        helperText: "Provide a clear, descriptive name for this SOP",
        showHelperTooltip: true,
        tooltipText:
          "Enter a clear, descriptive name that helps users identify the purpose of this Standard Operating Procedure.",
        disabled: readOnly,
        className: "col-span-3",
        classes: {
          container: "w-full mb-6",
          label: "block text-base font-semibold text-gray-800 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-lg font-medium placeholder-gray-400",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "shortName",
        label: "Short Name",
        type: "text",
        required: true,
        placeholder: "e.g. WMI",
        helperText: "Abbreviated version for quick reference",
        showHelperTooltip: true,
        tooltipText:
          "Provide a short abbreviation or acronym that can be used for quick reference in lists and reports.",
        disabled: readOnly,
        className: "col-span-3",
        classes: {
          container: "w-full mb-6",
          label: "block text-base font-semibold text-gray-800 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-lg font-medium placeholder-gray-400",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Briefly describe the purpose and scope of this SOP...",
        rows: 3,
        helperText: "Provide a brief overview of what this SOP covers",
        showHelperTooltip: true,
        tooltipText:
          "Describe the purpose and scope of this SOP. This helps users understand when and how to use this procedure.",
        disabled: readOnly,
        className: "col-span-6",
        classes: {
          container: "w-full mb-6",
          label: "block text-base font-semibold text-gray-800 mb-2",
          input:
            "w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-base placeholder-gray-400 resize-vertical",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        fullWidth: true,
      },
      {
        name: "utilityType",
        label: "Utility Type",
        type: "select",
        required: true,
        placeholder: "Select Utility Type ",
        options: utilityOptions,
        helperText: "Select the primary utility service this SOP applies to",
        showHelperTooltip: true,
        tooltipText:
          "Choose the specific utility type this SOP is designed for. This helps categorize and organize your procedures.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        fullWidth: true,
      },
      {
        name: "associatedWith",
        label: "Service Association",
        type: "select",
        required: true,
        options: associationOptions,
        placeholder: "Select association",
        helperText: "Type of service this SOP is associated with",
        showHelperTooltip: true,
        tooltipText:
          "Choose what type of service or entity this SOP is designed to work with. This determines which additional fields will be available.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "operationType",
        label: "Operation Type",
        type: "select",
        required: true,
        options: getOperationTypeOptions(),
        placeholder: "Select operation type",
        helperText: "Type of operation this SOP is associated with",
        showHelperTooltip: true,
        tooltipText:
          "Choose what type of operation or activity this SOP is designed for. This helps categorize the procedure.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        required: true,
        options: getTypeOptions(),
        placeholder: "Select type",
        helperText: "Main category for this SOP",
        showHelperTooltip: true,
        tooltipText:
          "Choose the primary category that best describes this SOP's purpose.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // Custom Type Field (conditional)
      ...(basicInfoForm.watch("type") === "custom"
        ? [
            {
              name: "customType",
              label: "Custom Type",
              type: "text" as const,
              required: true,
              placeholder: "Enter custom type",
              helperText: "Specify your custom type",
              disabled: readOnly,
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },
          ]
        : []),
      {
        name: "subType",
        label: "Subtype",
        type: "select",
        required: true,
        options: getSubtypeOptions(),
        disabled: readOnly || !selectedTypeCode,
        placeholder: selectedTypeCode ? "Select subtype" : "Select type first",
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly || !basicInfoForm.watch("typeCode")
              ? "bg-gray-50 cursor-not-allowed"
              : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // Custom Subtype Field (conditional)
      ...(basicInfoForm.watch("subType") === "custom"
        ? [
            {
              name: "customSubType",
              label: "Custom Subtype",
              type: "text" as const,
              required: true,
              placeholder: "Enter custom subtype",
              helperText: "Specify your custom subtype",
              disabled: readOnly,
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            },
          ]
        : []),
      {
        name: "sla",
        label: "SLA Rules",
        type: "select",
        options: getSLARulesOptions(),
        required: true,
        placeholder: "Select SLA Rules",
        helperText: "Service level agreement timeframe",
        showHelperTooltip: true,
        tooltipText:
          "Specify the expected completion time for this SOP procedure.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly || !basicInfoForm.watch("typeCode")
              ? "bg-gray-50 cursor-not-allowed"
              : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // SLA Hrs field
      // {
      //   name: "slaHrs",
      //   label: "SLA Hrs",
      //   type: "text",
      //   required: true,

      //   placeholder: "e.g. 48",
      //   helperText: "SLA in hours",
      //   showHelperTooltip: true,
      //   tooltipText: "Specify the SLA in hours if needed.",
      //   disabled: readOnly,
      //   className: "col-span-2",
      //   classes: {
      //     container: "w-full mb-4",
      //     label: "block text-sm font-medium text-gray-700 mb-2",
      //     input:
      //       "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
      //     error: "text-red-600 text-sm mt-1",
      //     helperText: "text-xs text-gray-500 mt-1",
      //   },
      // },
      {
        name: "estimatedLabourCost",
        label: `Estimated Labour Cost (${currency})`,
        type: "text",
        required: true,
        placeholder: "e.g. 5000",
        helperText: "Expected cost for labor",
        showHelperTooltip: true,
        tooltipText:
          "Enter the estimated labor cost required to complete this SOP procedure.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // estimited other cost
      {
        name: "estimatedOtherCost",
        label: `Estimated Other Cost (${currency})`,
        type: "text" as const,
        required: true,
        placeholder: "e.g. 5000",
        helperText: "Expected cost for other",
        showHelperTooltip: true,
        tooltipText:
          "Enter the estimated other cost required to complete this SOP procedure.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "taxRate",
        label: "Tax Rate (%)",
        type: "select",
        required: true,
        placeholder: "Select Tax Rate",
        helperText: "Applicable tax percentage",
        showHelperTooltip: true,
        tooltipText:
          "Enter the tax rate percentage that applies to this service.",
        disabled: readOnly,
        className: "col-span-2",
        options: getTaxRateOptions(),
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "requiredFieldforceCount",
        label: "Required Fieldforce Count",
        type: "text",
        required: true,
        placeholder: "e.g. 2",
        helperText: "Number of field personnel needed",
        showHelperTooltip: true,
        tooltipText:
          "Specify the number of field personnel required to execute this SOP.",
        disabled: readOnly,
        className: "col-span-2",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // Asset Configuration Fields (conditional)
      ...(showAssetFields
        ? [
            {
              name: "assetType",
              label: "Asset Type",
              type: "multi-select" as const,
              required: true,
              options: assetTypeOptions,
              maxDisplayItems: 2,
              placeholder: "Select asset type",
              showHelperTooltip: true,
              tooltipText:
                "Choose the specific type of asset that this SOP procedure will be applied to.",
              disabled: readOnly,
              className: "col-span-3",
              classes: {
                container: "w-full mb-4",
                label:
                  "block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1",
                select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
                  readOnly
                    ? "bg-gray-50 cursor-not-allowed text-gray-500"
                    : "bg-white"
                }`,
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs mt-1 text-gray-500",
              },
            },
            // Custom Asset Type Field (conditional)

            // Custom Action Type Field (conditional)
            ...(basicInfoForm.watch("actionType") === "custom"
              ? [
                  {
                    name: "customActionType",
                    label: "Custom Action Type",
                    type: "text" as const,
                    required: true,
                    placeholder: "Enter custom action type",
                    helperText: "Specify your custom action type",
                    disabled: readOnly,
                    classes: {
                      container: "w-full mb-4",
                      label: "block text-sm font-medium text-gray-700 mb-2",
                      input:
                        "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                      error: "text-red-600 text-sm mt-1",
                      helperText: "text-xs text-gray-500 mt-1",
                    },
                  },
                ]
              : []),

            // Custom Operation Type Field (conditional)
            ...(basicInfoForm.watch("operationType") === "custom"
              ? [
                  {
                    name: "customOperationType",
                    label: "Custom Operation Type",
                    type: "text" as const,
                    required: true,
                    placeholder: "Enter custom operation type",
                    helperText: "Specify your custom operation type",
                    disabled: readOnly,
                    classes: {
                      container: "w-full mb-4",
                      label: "block text-sm font-medium text-gray-700 mb-2",
                      input:
                        "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
                      error: "text-red-600 text-sm mt-1",
                      helperText: "text-xs text-gray-500 mt-1",
                    },
                  },
                ]
              : []),
          ]
        : []),
      ...(showSystemFields
        ? [
            {
              name: "systemType",
              label: "System Type",
              type: "multi-select" as const,
              required: true,
              options: systemOptions,
              placeholder: "Select system type",
              maxDisplayItems: 2,
              showHelperTooltip: true,
              tooltipText:
                "Choose the specific type of system that this SOP procedure will be applied to.",
              disabled: readOnly,
              className: "col-span-2",
            },
          ]
        : []),
      ...(showFacilityFields
        ? [
            {
              name: "facilityType",
              label: "Facility Type",
              type: "multi-select" as const,
              required: true,
              options: facilityTypeOptions,
              placeholder: "Select facility type",
              helperText: "Type of facility this SOP is for",
              showHelperTooltip: true,
              maxDisplayItems: 2,
              tooltipText:
                "Choose the specific type of facility that this SOP procedure will be applied to.",
              disabled: readOnly,
              className: "col-span-2",
            },
          ]
        : []),
      ...(showNetworkFields
        ? [
            {
              name: "networkType",
              label: "Network Type",
              type: "multi-select" as const,
              required: true,
              options: networkOptions,
              maxDisplayItems: 2,
              placeholder: "Select network type",
              showHelperTooltip: true,
              tooltipText:
                "Choose the specific type of network that this SOP procedure will be applied to.",
              disabled: readOnly,
              className: "col-span-2",
            },
          ]
        : []),
      ...(showUnit
        ? [
            {
              name: "unit",
              label: "Unit",
              type: "select" as const,
              required: true,
              options: finalUnitOptions,
              placeholder: "Select Unit",
              // helperText: "Type of network this SOP is for",
              showHelperTooltip: true,
              // tooltipText:
              //   "Choose the specific type of network that this SOP procedure will be applied to.",
              disabled: readOnly,
              className: "col-span-2 mt-2",
            },
          ]
        : []),
    ],
    [
      readOnly,
      showAssetFields,
      showFacilityFields,
      showUnit,
      showNetworkFields,
      showSystemFields,
      selectedTypeCode,
      associationOptions,
      utilityOptions,
      typeOptions,
      subtypeOptions,
      operationTypes,
      taxData,
      finalUnitOptions,
      flowcapacityUnitList,
      electricUnitList,
    ],
  );

  useEffect(() => {
    // Initialize selectedTypeCode from state when component mounts or state changes
    if (state.basicInfo?.typeCode && !selectedTypeCode) {
      setSelectedTypeCode(state.basicInfo.typeCode);
    }

    // Also handle the case where type is set but typeCode is not
    if (state.basicInfo?.type && !state.basicInfo?.typeCode) {
      const selectedTypeOption = typeOptions.find(
        (option) => option.value === state.basicInfo.type,
      );
      if (selectedTypeOption) {
        setSelectedTypeCode(selectedTypeOption.value);
      }
    }
  }, [
    state.basicInfo?.typeCode,
    state.basicInfo?.type,
    selectedTypeCode,
    typeOptions,
  ]);
  useEffect(() => {
    if (state.basicInfo && Object.keys(state.basicInfo).length > 0) {
      const formData = {
        sopName: state.basicInfo.sopName || "",
        shortName: state.basicInfo.shortName || "",
        description: state.basicInfo.description || "",
        utilityType: state.basicInfo.utilityType || "",
        type: state.basicInfo.type || "",
        typeCode: state.basicInfo.typeCode || "",
        subType: state.basicInfo.subType || "",
        subTypeCode: state.basicInfo.subTypeCode || "",
        associatedWith: state.basicInfo.associatedWith || "",
        sla: state.basicInfo.sla || "",
        estimatedLabourCost: state.basicInfo.estimatedLabourCost || "",
        taxRate: getTaxCodeFromApi(state.basicInfo.taxRate || ""),
        requiredFieldforceCount: state.basicInfo.requiredFieldforceCount || "",
        customAssetType: state.basicInfo.customAssetType || "",
        actionType: state.basicInfo.actionType || "",
        customActionType: state.basicInfo.customActionType || "",
        operationType: state.basicInfo.operationType || "",
        customOperationType: state.basicInfo.customOperationType || "",
        customType: state.basicInfo.customType || "",
        customSubType: state.basicInfo.customSubType || "",
        // ✅ Array fields with proper defaults
        assetType: state.basicInfo.assetType || [],
        systemType: state.basicInfo.systemType || [],
        facilityType: state.basicInfo.facilityType || [],
        networkType: state.basicInfo.networkType || [],
        unit: state.basicInfo.unit || "",
        basicInfoUpdated: state.basicInfo.basicInfoUpdated || false,
      };

      const currentValues = basicInfoForm.getValues();

      // ✅ FIXED: Proper comparison function that handles arrays and primitives
      const hasChanges = (() => {
        // ✅ Simple fields (strings, numbers) - REMOVED assetType from here
        const simpleFields = [
          "sopName",
          "shortName",
          "description",
          "utilityType",
          "type",
          "subType",
          "associatedWith",
          "sla",
          "estimatedLabourCost",
          "taxRate",
          "requiredFieldforceCount",
          "customAssetType",
          "actionType",
          "customActionType",
          "operationType",
          "customOperationType",
          "customType",
          "customSubType",
          "unit", // unit is a single select (string)
        ];

        // Check simple fields
        for (const field of simpleFields) {
          if (currentValues[field] !== formData[field]) {
            return true;
          }
        }

        // ✅ Array fields (need deep comparison) - ADDED assetType here
        const arrayFields = [
          "assetType",
          "systemType",
          "facilityType",
          "networkType",
        ];

        for (const field of arrayFields) {
          const currentArray = currentValues[field] || [];
          const newArray = formData[field] || [];

          // Compare array lengths first
          if (currentArray.length !== newArray.length) {
            return true;
          }

          // Compare array contents
          const arraysEqual =
            currentArray.length === newArray.length &&
            currentArray.every((val, idx) => val === newArray[idx]);

          if (!arraysEqual) {
            return true;
          }
        }

        return false;
      })();

      if (hasChanges) {
        basicInfoForm.reset(formData);

        setTimeout(() => {
          basicInfoForm.trigger();
        }, 100);
      }
    }
  }, [state.basicInfo, basicInfoForm, getTaxCodeFromApi]);
  // Auto mode detection in form submit handler
  const handleFormSubmit = useCallback(
    async (formData: z.infer<typeof basicInfoSchema>) => {
      try {
        // Check if we have an existing SOP ID (coming from other steps)
        const existingSopId = state.basicInfo?.sopId || state.apiResponse?.id;
        const isUpdateOperation = isEditMode || existingSopId;

        // Transform form data to API format with basicInfoUpdated flag for updates
        const apiData = transformFormDataToApiFormat(
          formData,
          isUpdateOperation,
        );
        const payload = convertToSnakeCase(apiData);

        let response;

        if (isUpdateOperation) {
          //  Handle Edit Mode or Update from other steps - Use updateSop
          const sopIdToUpdate = existingSopId || sopId;

          response = await updateSopMutation.mutateAsync({
            sopId: parseInt(sopIdToUpdate),
            data: payload as any, // Cast to match your update types
          });

          // Show success toast for update
          toast({
            title: "SOP Updated Successfully",
            description: `SOP "${
              response.result?.name || formData.sopName
            }" has been updated`,
            duration: 3000,
          });

          // ✅ Enhanced: Update parent state with the updated data INCLUDING LABELS
          const updatedBasicInfo = {
            ...formData,
            sopId: sopIdToUpdate,
            serviceNo: response.result?.serviceNo || state.basicInfo?.serviceNo,
            version: response.result?.version || state.basicInfo?.version,
            status: response.result?.statusDisplay || state.basicInfo?.status,
            taxRate: getTaxCodeFromApi(response.result?.tax),
            basicInfoUpdated: true,

            // ✅ NEW: Include all the labels from apiData.displayLabels
            utilityTypeLabel: apiData.displayLabels?.utilityTypeLabel,
            associatedWithLabel: apiData.displayLabels?.associatedWithLabel,
            typeLabel: apiData.displayLabels?.typeLabel,
            subTypeLabel: apiData.displayLabels?.subTypeLabel,
            operationTypeLabel: apiData.displayLabels?.operationTypeLabel,
            slaLabel: apiData.displayLabels?.slaLabel,
            taxRateLabel: apiData.displayLabels?.taxRateLabel,
          };

          onUpdateState({
            basicInfo: updatedBasicInfo,
            apiResponse: response.result,
          });
        } else {
          //  Handle Create Mode - Use createSop

          response = await createSopMutation.mutateAsync(payload);

          // Show success toast for creation
          toast({
            title: "SOP Created Successfully",
            description: `SOP "${response.result.name}" has been created with ID: ${response.result.id}`,
            duration: 3000,
          });

          // ✅ Enhanced: Update parent state with the created SOP ID and API response data INCLUDING LABELS
          const updatedBasicInfo = {
            ...formData,
            sopId: response.result.id,
            serviceNo: response.result.serviceNo,
            version: response.result.version,
            status: response.result.statusDisplay,
            basicInfoUpdated: false, // Set to false for new creations

            // ✅ NEW: Include all the labels from apiData.displayLabels
            utilityTypeLabel: apiData.displayLabels?.utilityTypeLabel,
            associatedWithLabel: apiData.displayLabels?.associatedWithLabel,
            typeLabel: apiData.displayLabels?.typeLabel,
            subTypeLabel: apiData.displayLabels?.subTypeLabel,
            operationTypeLabel: apiData.displayLabels?.operationTypeLabel,
            slaLabel: apiData.displayLabels?.slaLabel,
            taxRateLabel: apiData.displayLabels?.taxRateLabel,
          };

          onUpdateState({
            basicInfo: updatedBasicInfo,
            apiResponse: response.result,
          });
          basicInfoForm.reset(updatedBasicInfo);
          if (onStepSave) {
            await onStepSave(0, updatedBasicInfo, true); // This will mark step as completed AND move to next
          }
        }
        // if (onNext) {
        //   onNext();
        // }

        // Proceed to next step
      } catch (error: any) {
        // Show error toast
        const errorMessage =
          error?.message ||
          (isEditMode || state.basicInfo?.sopId
            ? "An error occurred while updating the SOP. Please try again."
            : "An error occurred while creating the SOP. Please try again.");

        toast({
          title:
            isEditMode || state.basicInfo?.sopId
              ? "Failed to Update SOP"
              : "Failed to Create SOP",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    [
      transformFormDataToApiFormat,
      isEditMode,
      sopId,
      createSopMutation,
      updateSopMutation,
      onUpdateState,
      onNext,
      toast,
      state.basicInfo,
      state.apiResponse,
      onStepSave,
    ],
  );
  const handleFinalFormSubmit = useCallback(
    async (formData: z.infer<typeof basicInfoSchema>) => {
      if (!basicInfoForm.formState.isSubmitting) return;
      try {
        const existingSopId = state.basicInfo?.sopId || state.apiResponse?.id;
        const isUpdateOperation = isEditMode || existingSopId;

        const apiData = transformFormDataToApiFormat(
          formData,
          isUpdateOperation,
        );
        const payload = convertToSnakeCase(apiData);

        let response;

        if (isUpdateOperation) {
          // UPDATE SOP
          const sopIdToUpdate = existingSopId || sopId;

          response = await updateSopMutation.mutateAsync({
            sopId: parseInt(sopIdToUpdate),
            data: payload as any, // Cast to match your update types
          });

          // Show success toast for update
          toast({
            title: "SOP Updated Successfully",
            description: `SOP "${
              response.result?.name || formData.sopName
            }" has been updated`,
            duration: 3000,
          });

          // ✅ Enhanced: Update parent state with the updated data INCLUDING LABELS
          const updatedBasicInfo: any = {
            ...formData,
            sopId: sopIdToUpdate,
            serviceNo: response.result?.serviceNo || state.basicInfo?.serviceNo,
            version: response.result?.version || state.basicInfo?.version,
            status: response.result?.statusDisplay || state.basicInfo?.status,
            taxRate: getTaxCodeFromApi(response.result?.tax),
            basicInfoUpdated: true,
            // ✅ NEW: Include all the labels from apiData.displayLabels
            utilityTypeLabel: apiData.displayLabels?.utilityTypeLabel,
            associatedWithLabel: apiData.displayLabels?.associatedWithLabel,
            typeLabel: apiData.displayLabels?.typeLabel,
            subTypeLabel: apiData.displayLabels?.subTypeLabel,
            operationTypeLabel: apiData.displayLabels?.operationTypeLabel,
            slaLabel: apiData.displayLabels?.slaLabel,
            taxRateLabel: apiData.displayLabels?.taxRateLabel,
            unit: formData.unit,
            networkType: formData.networkType ?? [],
            facilityType: formData.facilityType ?? [],
            systemType: formData.systemType ?? [],
            assetType: formData.assetType ?? [],
          };

          if (formData.unit) {
            updatedBasicInfo.unitReadings = [
              {
                name: "Current Reading",
                unit: formData.unit,
              },
            ];
          }

          onUpdateState({
            basicInfo: updatedBasicInfo,
            apiResponse: response.result,
          });

          basicInfoForm.reset(updatedBasicInfo);
          if (onStepSave) {
            await onStepSave(0, updatedBasicInfo, true); // This will mark step as completed AND move to next
          }

          if (onNext) {
            onNext();
          }
        } else {
          // CREATE SOP
          response = await createSopMutation.mutateAsync(payload);

          toast({
            title: "SOP Created Successfully",
            description: `SOP "${response.result.name}" has been created with ID: ${response.result.id}`,
          });

          const updatedBasicInfo: any = {
            ...formData,
            sopId: response.result.id,
            serviceNo: response.result.serviceNo,
            version: response.result.version,
            status: response.result.statusDisplay,
            basicInfoUpdated: false,

            utilityTypeLabel: apiData.displayLabels?.utilityTypeLabel,
            associatedWithLabel: apiData.displayLabels?.associatedWithLabel,
            typeLabel: apiData.displayLabels?.typeLabel,
            subTypeLabel: apiData.displayLabels?.subTypeLabel,
            operationTypeLabel: apiData.displayLabels?.operationTypeLabel,
            slaLabel: apiData.displayLabels?.slaLabel,
            taxRateLabel: apiData.displayLabels?.taxRateLabel,
            unit: formData.unit,
            networkType: formData.networkType ?? [],
            facilityType: formData.facilityType ?? [],
            systemType: formData.systemType ?? [],
            assetType: formData.assetType ?? [],
          };
          if (formData.unit) {
            updatedBasicInfo.unitReadings = [
              {
                name: "Current Reading",
                unit: formData.unit,
              },
            ];
          }

          onUpdateState({
            basicInfo: updatedBasicInfo,
            apiResponse: response.result,
          });

          basicInfoForm.reset(updatedBasicInfo);

          if (onStepSave) {
            await onStepSave(0, updatedBasicInfo, true);
          }
          if (onNext) {
            onNext();
          }
        }
      } catch (error: any) {
        toast({
          title: "SOP Error",
          description: error?.message || "Something went wrong",
          variant: "destructive",
        });
      }
    },
    [
      transformFormDataToApiFormat,
      isEditMode,
      sopId,
      createSopMutation,
      updateSopMutation,
      onUpdateState,
      toast,
      state.basicInfo,
      state.apiResponse,
      onStepSave,
    ],
  );

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (fieldName === "type") {
        const selectedTypeOption = typeOptions.find(
          (option) => option.value === value,
        );

        if (selectedTypeOption) {
          setSelectedTypeCode(selectedTypeOption.value);

          const updatedData = {
            ...state.basicInfo,
            type: value,
            typeCode: selectedTypeOption.value,
            typeLabel: selectedTypeOption.label, // ✅ Store label
            subType: "",
            subTypeCode: "",
            subTypeLabel: "", // ✅ Clear subtype label
          };
          onUpdateState({ basicInfo: updatedData });
        } else if (value === "custom") {
          setSelectedTypeCode("");
          const updatedData = {
            ...state.basicInfo,
            type: value,
            typeCode: "",
            typeLabel: "Custom", // ✅ Store label for custom
            subType: "",
            subTypeCode: "",
            subTypeLabel: "",
          };
          onUpdateState({ basicInfo: updatedData });
        }
      } else {
        // ✅ Enhanced field change handling with label lookup
        let updatedData = { ...state.basicInfo, [fieldName]: value };

        // Store labels for dropdown fields
        if (fieldName === "utilityType") {
          const utilityOption = utilityOptions.find(
            (option) => option.value === value,
          );
          updatedData.utilityTypeLabel = utilityOption?.label || value;
        } else if (fieldName === "associatedWith") {
          const associationMap = {
            "0": "Consumer",
            "1": "Meter",
            "2": "Asset",
            "3": "Complaint",
            "4": "Generic",
          };
          updatedData.associatedWithLabel = associationMap[value] || value;
        } else if (fieldName === "subType") {
          const subtypeOption = subtypeOptions.find(
            (option) => option.value === value,
          );
          updatedData.subTypeLabel = subtypeOption?.label || value;
        } else if (fieldName === "operationType") {
          const operationOption = getOperationTypeOptions().find(
            (option) => option.value === value,
          );
          updatedData.operationTypeLabel = operationOption?.label || value;
        } else if (fieldName === "sla") {
          const slaOption = getSLARulesOptions().find(
            (option) => option.value === value,
          );
          updatedData.slaLabel = slaOption?.label || value;
        } else if (fieldName === "taxRate") {
          const taxOption = getTaxRateOptions().find(
            (option) => option.value === value,
          );
          updatedData.taxRateLabel = taxOption?.label || value;
        } else if (fieldName === "systemType") {
          const selectedLabels = (value as string[])
            .map((val) => systemOptions.find((opt) => opt.value === val)?.label)
            .filter(Boolean);
          updatedData.systemTypeLabel = selectedLabels.join(", ");
        } else if (fieldName === "facilityType") {
          const selectedLabels = (value as string[])
            .map(
              (val) =>
                facilityTypeOptions.find((opt) => opt.value === val)?.label,
            )
            .filter(Boolean);
          updatedData.facilityTypeLabel = selectedLabels.join(", ");
        } else if (fieldName === "networkType") {
          const selectedLabels = (value as string[])
            .map(
              (val) => networkOptions.find((opt) => opt.value === val)?.label,
            )
            .filter(Boolean);
          updatedData.networkTypeLabel = selectedLabels.join(", ");
        } else if (fieldName === "unit") {
          const unitOption = finalUnitOptions.find(
            (opt) => opt.value === value,
          );
          updatedData.unitLabel = unitOption?.label || value;
        }

        onUpdateState({ basicInfo: updatedData });
      }
    },
    [
      typeOptions,
      subtypeOptions,
      utilityOptions,
      systemOptions,
      facilityTypeOptions,
      networkOptions,
      finalUnitOptions,
      state.basicInfo,
      onUpdateState,
      getSLARulesOptions,
      getTaxRateOptions,
      getOperationTypeOptions,
    ],
  );

  //  Auto mode detection in render custom footer
  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    // Get loading state from the appropriate mutation based on auto-detected mode
    const isSubmitting = isEditMode
      ? updateSopMutation.isPending
      : createSopMutation.isPending;

    return (
      <div className="pt-6 flex justify-end border-t border-gray-200">
        <Button
          disabled={isSubmitting}
          type="submit"
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          onClick={() => handleFinalFormSubmit(basicInfoForm.getValues())}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {isEditMode ? "Updating SOP..." : "Creating SOP..."}
            </>
          ) : (
            <>
              {isEditMode ? "Update & Continue" : "Next"}{" "}
              <ChevronRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    );
  }, [
    readOnly,
    onPrevious,
    isEditMode,
    createSopMutation.isPending,
    updateSopMutation.isPending,
  ]);

  return (
    <div className="space-y-6 pt-4 p-4">
      {/* Unified Form with All Fields */}
      <DynamicForm
        fields={basicInfoFormFields}
        form={basicInfoForm}
        onSubmit={handleFinalFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
        gridColumns={6}
        responsiveColumns={{ sm: 1, md: 1 }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />

      {/* Asset Configuration Section Header - appears when Asset is selected */}
      {showAssetFields && (
        <div className="bg-slate-50 p-4 rounded-md border border-slate-200 -mt-4">
          <h3 className="font-medium text-slate-700 mb-2">
            Asset-Specific Fields
          </h3>
          <p className="text-sm text-slate-500">
            These fields are required when the service is associated with an
            Asset
          </p>
        </div>
      )}
    </div>
  );
};

export default SopBasicInfoStep;
